/**
 * The state-wide KYC review queue (STATE-SCOPE F2, ADR-0124). The review queue is read one market at a time (market RLS: `GET /v1/kyc/review-queue?market=`), and a
 * reviewer granted at a STATE reads exactly the markets the registry places in it. When the top selector is on a state the console therefore reads each market of the
 * state (a bounded fan-out, `REGION_READ_MAX_MARKETS`, like the dashboards) and merges the answers, newest first.
 *
 * NEVER SILENT: a market whose read failed, and markets beyond the fan-out bound, are reported (`failedMarkets`, `omittedMarkets`) so the screen marks the list partial.
 * If EVERY read failed there is nothing honest to show and the first error is returned.
 */
import type { ApiError, KycReviewDocument, OperatingMarketView } from '@vello/api-client';
import type { Result } from '@vello/core';
import { REGION_READ_MAX_MARKETS } from './ops-console.js';
import { marketsOfRegion } from './scope.js';

export interface StateReviewQueue {
  /** The merged documents, newest first. */
  readonly documents: readonly KycReviewDocument[];
  /** The markets whose queue could not be read (the list is partial while any). */
  readonly failedMarkets: readonly string[];
  /** How many markets of the state were not read because the state has more than the fan-out bound (the list is partial while > 0). */
  readonly omittedMarkets: number;
  /** The markets that were read (to take the document-type catalog from the first). */
  readonly readMarkets: readonly string[];
}

export interface ReviewQueueReader {
  reviewQueue(market?: string): Promise<Result<{ documents: readonly KycReviewDocument[] }, ApiError>>;
}

/** Merge per-market queues newest first (ties by id, so the order is stable). A document seen twice is kept once. */
export function mergeReviewQueues(queues: readonly (readonly KycReviewDocument[])[]): readonly KycReviewDocument[] {
  const seen = new Set<string>();
  const all: KycReviewDocument[] = [];
  for (const q of queues) for (const d of q) if (!seen.has(d.id)) { seen.add(d.id); all.push(d); }
  return all.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : a.id.localeCompare(b.id)));
}

/** Read and merge the queue of every (bounded) market of `region`. A state with no registered market answers an empty, complete queue. */
export async function loadStateReviewQueue(
  api: ReviewQueueReader, region: string, markets: readonly OperatingMarketView[],
): Promise<Result<StateReviewQueue, ApiError>> {
  const all = marketsOfRegion(region, markets).map((m) => m.id);
  const ids = all.slice(0, REGION_READ_MAX_MARKETS);
  const answers = await Promise.all(ids.map((m) => api.reviewQueue(m)));
  const ok: { id: string; documents: readonly KycReviewDocument[] }[] = [];
  const failedMarkets: string[] = [];
  let firstError: ApiError | undefined;
  answers.forEach((a, i) => {
    const id = ids[i] as string;
    if (a.ok) ok.push({ id, documents: a.value.documents });
    else {
      failedMarkets.push(id);
      firstError ??= a.error;
    }
  });
  if (ids.length > 0 && ok.length === 0 && firstError !== undefined) return { ok: false, error: firstError };
  return {
    ok: true,
    value: { documents: mergeReviewQueues(ok.map((o) => o.documents)), failedMarkets, omittedMarkets: all.length - ids.length, readMarkets: ok.map((o) => o.id) },
  };
}
